/**
 * 판정 가이드(PROVE 확정 전) — 결정트리 사전 점검 결과와 국내 결정례 힌트를 사람이 읽는 네 칸으로 엮는다.
 *
 *   1. 지금 경로(류 → 호 → 소호 → 세번)와 그 라벨
 *   2. 뒷받침 결정례: 같은 경로로 간 국내 결정례
 *   3. 갈림 결정례: 같은 류인데 다른 호·소호로 간 결정례와, 그 갈림을 가르는 트리 질문(가르는 사실)
 *   4. 신고 전 확인: 아직 답하지 않은 트리 질문
 *
 * 전부 결정트리 출력과 결정례 집계에서 결정론적으로 만든다. LLM은 쓰지 않으며 세번·법적 결론을 새로 만들지
 * 않는다(PROVE 총합본: 제안은 뉴럴, 확정은 심볼릭). 결정례는 근거일 뿐 규칙이 아니다.
 */

import { isProductQuestion } from './productFactDialogue';
const digits = (value) => String(value || "").replace(/[^0-9]/g, "");

/** "6210 · 의류(제5903호 …)" → "6210", "6211.43 · …" → "621143". 코드 접두가 없으면 "". */
export function optionCode(label) {
  const m = String(label || "").match(/^(\d{4})(?:\.(\d{2}))?(?:-(\d{4}))?\s*·/);
  if (!m) return "";
  return `${m[1]}${m[2] || ""}${m[3] || ""}`;
}

export function formatCode(value) {
  const d = digits(value);
  if (d.length >= 10) return `${d.slice(0, 4)}.${d.slice(4, 6)}-${d.slice(6, 10)}`;
  if (d.length >= 6) return `${d.slice(0, 4)}.${d.slice(4, 6)}`;
  if (d.length >= 4) return d.slice(0, 4);
  return d;
}

function normalizeQuestion(question) {
  if (!question || typeof question !== "object" || !question.fact_key) return null;
  const optionLabels = question.option_labels && typeof question.option_labels === "object" ? question.option_labels : {};
  const options = (Array.isArray(question.options) ? question.options : []).map((option) => {
    const value = option && typeof option === "object" ? option.value : option;
    const label = option && typeof option === "object"
      ? option.label || optionLabels[String(option.value)] || String(option.value)
      : optionLabels[String(option)] || String(option);
    return { value: String(value), label: String(label), code: optionCode(label) };
  });
  return {
    key: question.fact_key,
    prompt: question.prompt_ko || question.prompt || "",
    options,
    deferred: Boolean(question.deferred),
    secondary: Boolean(question.secondary),
  };
}

/** 라벨 색인: 트리가 내려준 option_labels·fact_value_labels에서 코드 → 한글 라벨. */
function labelIndex(tree) {
  const index = {};
  const collect = (labels) => {
    if (!labels || typeof labels !== "object") return;
    Object.values(labels).forEach((label) => {
      const code = optionCode(label);
      if (code && !index[code]) index[code] = String(label).replace(/^[\d.\-]+\s*·\s*/, "");
    });
  };
  [...(Array.isArray(tree?.questions) ? tree.questions : []), ...(Array.isArray(tree?.editable_facts) ? tree.editable_facts : [])]
    .forEach((entry) => collect(entry && entry.option_labels));
  if (tree?.fact_value_labels && typeof tree.fact_value_labels === "object") {
    Object.values(tree.fact_value_labels).forEach(collect);
  }
  return index;
}

/**
 * @param {object} tree  decision_tree_shadow
 * @param {object|null} hints precedent_hints ({ by_hsk: [{hsk, count, latest_date, label, desc}] })
 * @param {object} answers 사용자가 카드에서 답한 사실(키 → 값)
 */
export function buildPrecedentGuide({ tree, hints, answers = {} }) {
  if (!tree || typeof tree !== "object") return null;
  const chapter = digits(tree.supported_chapter).slice(0, 2)
    || digits(tree.supported_hs4).slice(0, 2)
    || (Array.isArray(tree.hs4_candidates) && tree.hs4_candidates.length ? digits(tree.hs4_candidates[0]).slice(0, 2) : "");
  const hs4 = digits(tree.supported_hs4).slice(0, 4);
  const hs6 = digits(tree.supported_hs6).slice(0, 6);
  const hsk = digits(tree.supported_hsk).slice(0, 10);
  const current = hsk || hs6 || hs4;
  if (!chapter && !current) return null;

  const labels = labelIndex(tree);
  const path = {
    chapter,
    hs4,
    hs6,
    hsk,
    current,
    label: [
      chapter && `제${chapter}류`,
      hs4 && `${hs4}호${labels[hs4] ? ` · ${labels[hs4]}` : ""}`,
      hs6 && `${formatCode(hs6)}${labels[hs6] ? ` · ${labels[hs6]}` : ""}`,
      hsk && formatCode(hsk),
    ].filter(Boolean).join(" → "),
    action: String(tree.action || ""),
  };

  const rows = Array.isArray(hints?.by_hsk) ? hints.by_hsk.filter((row) => row && row.hsk) : [];
  const supporting = [];
  const divergingByCode = new Map();
  rows.forEach((row) => {
    const code = digits(row.hsk);
    const count = Number(row.count || 0);
    if (!code || count <= 0) return;
    if (current && code.startsWith(current)) {
      supporting.push({ hsk: code, count, latest_date: row.latest_date || "", label: row.label || "" });
      return;
    }
    if (!chapter || !code.startsWith(chapter)) return;
    // 갈림은 호(또는 소호가 정해졌으면 소호) 단위로 묶는다.
    const groupKey = hs6 ? code.slice(0, 6) : code.slice(0, 4);
    const existing = divergingByCode.get(groupKey) || { code: groupKey, count: 0, latest_date: "", label: "", samples: [] };
    existing.count += count;
    if (String(row.latest_date || "") > existing.latest_date) {
      existing.latest_date = String(row.latest_date || "");
      existing.label = row.label || "";
    }
    if (existing.samples.length < 2 && row.label) existing.samples.push(row.label);
    divergingByCode.set(groupKey, existing);
  });
  const diverging = [...divergingByCode.values()]
    .sort((a, b) => b.count - a.count || (b.latest_date > a.latest_date ? 1 : -1))
    .slice(0, 3)
    .map((entry) => ({ ...entry, display: formatCode(entry.code), name: labels[entry.code] || "" }));

  const questionSource = tree.product_fact_dialogue?.questions || tree.questions;
  const questions = (Array.isArray(questionSource) ? questionSource : []).filter(isProductQuestion).map(normalizeQuestion).filter(Boolean);
  const answered = (key) => answers && answers[key] !== undefined && answers[key] !== null && answers[key] !== "";
  const open = questions.filter((q) => !q.deferred && !answered(q.key));
  const primaryOpen = open.filter((q) => !q.secondary);

  // 가르는 사실: 갈림 결정례의 코드를 선택지로 가진 트리 질문. 없으면 지금 답할 첫 질문.
  let decidingFact = null;
  let decidingReason = "";
  if (diverging.length > 0) {
    const targetCodes = diverging.map((d) => d.code);
    const hit = questions.find((q) => q.options.some((o) => o.code && targetCodes.some((code) => code.startsWith(o.code) || o.code.startsWith(code))));
    if (hit) {
      decidingFact = hit;
      decidingReason = "option_code";
    }
  }
  if (!decidingFact && primaryOpen.length > 0) {
    decidingFact = primaryOpen[0];
    decidingReason = "first_open";
  }

  const checklist = [];
  primaryOpen.forEach((q) => checklist.push({ kind: "fact", text: q.prompt, key: q.key }));
  open.filter((q) => q.secondary).forEach((q) => checklist.push({ kind: "legal", text: q.prompt, key: q.key }));
  diverging.forEach((d) => checklist.push({
    kind: "diverging",
    text: `${d.display}${d.name ? ` · ${d.name}` : ""}로 간 결정례 ${d.count}건이 있습니다.${decidingFact && decidingReason === "option_code" ? ` 가르는 사실: ${decidingFact.prompt}` : ""}`,
    code: d.code,
  }));
  const deferredCount = questions.filter((q) => q.deferred).length;
  if (deferredCount > 0) checklist.push({ kind: "deferred", text: `위 사실을 확인하면 이어지는 질문 ${deferredCount}개가 있습니다.` });

  const supportingCount = supporting.reduce((sum, row) => sum + row.count, 0);
  return {
    path,
    supporting: supporting.sort((a, b) => b.count - a.count).slice(0, 5),
    supportingCount,
    diverging,
    decidingFact: decidingFact ? { key: decidingFact.key, prompt: decidingFact.prompt, reason: decidingReason } : null,
    checklist,
    isEmpty: supportingCount === 0 && diverging.length === 0 && checklist.length === 0,
  };
}

/** 검토보고서 페이로드용(서버가 그대로 그린다). */
export function guideForReport(guide) {
  if (!guide || guide.isEmpty) return null;
  return {
    path: guide.path.label,
    status: guide.path.action,
    supporting: guide.supporting.map((row) => ({
      hsk: row.hsk, count: row.count, latest_date: row.latest_date, label: row.label,
    })),
    supporting_count: guide.supportingCount,
    diverging: guide.diverging.map((d) => ({ code: d.display, name: d.name, count: d.count, latest_date: d.latest_date, sample: d.samples[0] || "" })),
    deciding_fact: guide.decidingFact ? guide.decidingFact.prompt : "",
    checklist: guide.checklist.map((item) => item.text),
  };
}
